import React, {useState} from 'react';
import {Checkbox} from 'antd';
import DiffViewer from '../JsonDiff';

const FIELDS_TO_SKIP = [
  'id',
  'updated_at',
  'sponsorship_perk.id',
  'sponsorship_perk.updated_at',
  'sponsorship_perk.created_at',
  'sponsorship_perk.create_by_id',
  'sponsorship_perk.edit_by_id',
  'sponsorship_perk.event_option_id',
];

const LARGE_TEXT_FIELDS = ['email_template_body'];
const LARGE_TEXT_PREVIEW_LENGTH = 160;

const stripHtmlForPreview = (html) =>
  String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const summarizeLargeTextFields = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  const out = Array.isArray(obj) ? [...obj] : {...obj};

  LARGE_TEXT_FIELDS.forEach((field) => {
    if (!(field in out) || out[field] == null) return;
    const raw = String(out[field]);
    const plain = stripHtmlForPreview(raw);
    const preview =
      plain.length > LARGE_TEXT_PREVIEW_LENGTH
        ? `${plain.slice(0, LARGE_TEXT_PREVIEW_LENGTH)}…`
        : plain;
    out[field] = `[Email template body — ${raw.length} characters] ${preview}`;
  });

  return out;
};

const omitPaths = (obj, paths) => {
  if (!obj || typeof obj !== 'object') return obj;
  const out = Array.isArray(obj) ? [...obj] : {...obj};

  paths.forEach((path) => {
    const parts = path.split('.');
    if (parts.length === 1) {
      delete out[parts[0]];
      return;
    }
    let current = out;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      if (
        current[key] &&
        typeof current[key] === 'object' &&
        !Array.isArray(current[key])
      ) {
        current = current[key];
      } else {
        return;
      }
    }
    delete current[parts[parts.length - 1]];
  });

  return out;
};

/**
 * Treat "", "N/A", whitespace-only strings as null so the diff doesn't
 * show noisy changes when only the empty representation changed.
 */
const EMPTY_STRING_SENTINELS = new Set(['', 'n/a', 'na', 'null', '-', '—']);

const normalizeEmptyForDiff = (value) => {
  if (value === undefined) return null;
  if (value === null) return null;
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '') return null;
    if (EMPTY_STRING_SENTINELS.has(t.toLowerCase())) return null;
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => normalizeEmptyForDiff(item));
  }
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = normalizeEmptyForDiff(value[k]);
    });
    return out;
  }
  return value;
};

/** Sort object keys so compare is by value, not property order in stored JSON. */
const sortKeysForDiff = (value) => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map((item) => sortKeysForDiff(item));
  }
  if (typeof value === 'object') {
    const sorted = {};
    Object.keys(value)
      .sort()
      .forEach((key) => {
        sorted[key] = sortKeysForDiff(value[key]);
      });
    return sorted;
  }
  return value;
};

const Diff = ({oldValue, newValue}) => {
  const [splitView, setSplitView] = useState(true);

  const toggleSplitView = () => {
    setSplitView((prev) => !prev);
  };

  const oldFiltered = summarizeLargeTextFields(omitPaths(oldValue, FIELDS_TO_SKIP));
  const newFiltered = summarizeLargeTextFields(omitPaths(newValue, FIELDS_TO_SKIP));
  const oldNormalized = sortKeysForDiff(normalizeEmptyForDiff(oldFiltered));
  const newNormalized = sortKeysForDiff(normalizeEmptyForDiff(newFiltered));
  const oldStr = JSON.stringify(oldNormalized, null, 2);
  const newStr = JSON.stringify(newNormalized, null, 2);
  const hasDifference = oldStr !== newStr;

  return (
    <div className='diff-container' style={{height: '100%'}}>
      <div
        className='diff-controls'
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: 8,
        }}>
        <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
          <Checkbox
            checked={splitView}
            onChange={(e) => setSplitView(e.target.checked)}
            style={{
              transform: 'scale(0.9)',
              transformOrigin: 'center',
            }}></Checkbox>
          <span
            role='button'
            tabIndex={0}
            onClick={toggleSplitView}
            onKeyDown={(e) => e.key === 'Enter' && toggleSplitView()}
            style={{cursor: 'pointer', fontSize: 12}}>
            Split View
          </span>
        </div>
      </div>
      <div
        className='audit-log-diff-scroll'
        style={{
          overflowY: 'auto',
          height: 'calc(100% - 36px)',
          overflowX: 'auto',
        }}>
        <DiffViewer
          oldValue={oldStr}
          newValue={newStr}
          splitView={splitView}
          disableWordDiff
          showDiffOnly={hasDifference}
          hideLineNumbers
          leftTitle={splitView ? 'Old Data' : 'Data'}
          rightTitle={splitView ? 'New Data' : ''}
        />
      </div>
    </div>
  );
};

export default Diff;
